import { supabase } from '@/integrations/supabase/client';
import { getVideoStorageProvider } from '@/services/videoStorageProvider';
import { monitoring } from '@/lib/monitoring';
import {
  TrainingVideo,
  VideoAnnotation,
  VideoUploadOptions,
  VideoUploadResponse,
  AnnotationCreateParams,
} from '@/types/videoAnnotations';
import { emit } from '@/integrations/analytics/emit';
import { VIDEO_UPLOAD_STARTED, VIDEO_UPLOAD_FAILED } from '@/integrations/analytics/events';

const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500 MB
const MAX_AUDIO_SIZE = 25 * 1024 * 1024;  // 25 MB
const MAX_VIDEO_DURATION_SECONDS = 600;   // 10 min
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ALLOWED_AUDIO_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg'];

// Reserva os últimos 10% para o INSERT em training_videos, deixando 0–90%
// dedicados ao transfer do arquivo. Evita regressão visual (90→80→100).
const UPLOAD_TRANSFER_MAX_PERCENT = 90;
const UPLOAD_COMPLETE_PERCENT = 100;
const SIGNED_URL_TTL_SECONDS = 3600;
const TRAINING_VIDEOS_BUCKET = 'training-videos';
const VIDEO_AUDIO_BUCKET = 'video-audio';

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const validateVideoFile = (file: File): { isValid: boolean; error?: string } => {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return { isValid: false, error: 'Formato não suportado. Use MP4, MOV ou WebM.' };
  }
  if (file.size > MAX_VIDEO_SIZE) {
    return { isValid: false, error: 'Vídeo muito grande. Máximo de 500 MB.' };
  }
  return { isValid: true };
};

export const validateVideoDuration = (file: File): Promise<{ isValid: boolean; durationSeconds?: number; error?: string }> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > MAX_VIDEO_DURATION_SECONDS) {
        resolve({ isValid: false, error: 'O vídeo excede o limite de 10 minutos. Selecione um clipe mais curto.' });
      } else {
        resolve({ isValid: true, durationSeconds: Math.round(video.duration) });
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      // The browser couldn't decode the video metadata. Most commonly this is
      // HEVC/H.265 video recorded on a phone (saved as .mp4, passes the MIME
      // check, but Chrome/Firefox can't load the metadata). Blocking here is
      // worse than letting Supabase Storage decide — the file is almost always
      // valid. We allow the upload to proceed and capture to Sentry for
      // volume tracking.
      monitoring.captureException(
        new Error('[validateVideoDuration] video.onerror: browser could not read metadata — upload proceeding without duration check'),
        {
          stage:        'validate_duration_onerror',
          file_type:    file.type,
          file_size_mb: +(file.size / 1_048_576).toFixed(2),
        },
      );
      resolve({ isValid: true });
    };
    video.src = url;
  });
};

export const validateAudioBlob = (blob: Blob): { isValid: boolean; error?: string } => {
  if (!ALLOWED_AUDIO_TYPES.some(t => blob.type.startsWith(t.split('/')[0]))) {
    return { isValid: false, error: 'Formato de áudio não suportado.' };
  }
  if (blob.size > MAX_AUDIO_SIZE) {
    return { isValid: false, error: 'Áudio muito grande. Máximo de 25 MB.' };
  }
  return { isValid: true };
};

// ---------------------------------------------------------------------------
// Video upload
// ---------------------------------------------------------------------------

export const uploadTrainingVideo = async (
  options: VideoUploadOptions & { durationSeconds?: number }
): Promise<VideoUploadResponse> => {
  const { file, title, description, reviewerId, onProgress, durationSeconds, context } = options;
  // The training_videos row is created only after a successful upload, so
  // both started/failed are PostHog-owned (see ANALYTICS_EVENT_MAP.md).
  const analyticsContext = context ?? 'training';

  emit(VIDEO_UPLOAD_STARTED, { context: analyticsContext });

  try {
    const validation = validateVideoFile(file);
    if (!validation.isValid) {
      emit(VIDEO_UPLOAD_FAILED, {
        reason: 'invalid_file',
        file_size_bytes: file.size,
        context: analyticsContext,
      });
      return { success: false, error: validation.error };
    }

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.user) {
      emit(VIDEO_UPLOAD_FAILED, {
        reason: 'unauthenticated',
        file_size_bytes: file.size,
        context: analyticsContext,
      });
      return { success: false, error: 'Usuário não autenticado.' };
    }

    const user = session.user;

    // Single-stage pipeline: resumable tus upload straight to Supabase Storage.
    // Client-side compression was removed (see git history): FFmpeg.wasm
    // single-thread was too slow for 4K phone clips. The bucket cap (500 MB)
    // + the validator gate keeps uploads bounded; tus resumes on flaky networks.
    const fileExt  = file.name.split('.').pop() ?? 'mp4';
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    // Reserve the last 10 % of the bar for the DB INSERT so the user sees
    // movement past 90 % when the upload finishes — same UX contract the
    // checkout/checkout-resume flow relies on.
    const scaledOnProgress = onProgress
      ? (rawPercent: number) =>
          onProgress(Math.min(
            UPLOAD_TRANSFER_MAX_PERCENT,
            Math.round((rawPercent * UPLOAD_TRANSFER_MAX_PERCENT) / 100),
          ))
      : undefined;

    const storageProvider = getVideoStorageProvider();
    const { path: storedPath, error: storageError } = await storageProvider.upload(
      file,
      fileName,
      { contentType: file.type, onProgress: scaledOnProgress }
    );

    if (storageError || !storedPath) {
      monitoring.captureException(
        new Error(storageError ?? 'storage upload returned empty path'),
        {
          stage:         'storage_upload',
          fileName,
          fileSizeBytes: file.size,
          contentType:   file.type,
        },
      );
      emit(VIDEO_UPLOAD_FAILED, {
        reason: 'storage_upload',
        file_size_bytes: file.size,
        context: analyticsContext,
      });
      return { success: false, error: 'Falha ao fazer upload do vídeo. Tente novamente.' };
    }

    // Salva o path, não a URL pública — signed URLs geradas na reprodução
    const { data: videoRecord, error: insertError } = await supabase
      .from('training_videos')
      .insert({
        uploader_id: user.id,
        subject_id: user.id,
        reviewer_id: reviewerId ?? null,
        title,
        description: description ?? null,
        video_url: storedPath,
        file_size_bytes: file.size,
        duration_seconds: durationSeconds ?? null,
        status: 'pending_review',
      })
      .select()
      .single();

    if (insertError) {
      monitoring.captureException(insertError, {
        stage:      'db_insert',
        storedPath,
        reviewerId: reviewerId ?? null,
      });
      await supabase.storage.from(TRAINING_VIDEOS_BUCKET).remove([storedPath]);
      // The bucket file is removed above, so no training_videos row exists
      // for this attempt; the failure stays PostHog-owned.
      emit(VIDEO_UPLOAD_FAILED, {
        reason: 'db_insert',
        file_size_bytes: file.size,
        context: analyticsContext,
      });
      return { success: false, error: 'Falha ao salvar registro do vídeo.' };
    }

    onProgress?.(UPLOAD_COMPLETE_PERCENT);

    return { success: true, video: videoRecord as TrainingVideo };
  } catch (error) {
    monitoring.captureException(error, { stage: 'unexpected', function: 'uploadTrainingVideo' });
    emit(VIDEO_UPLOAD_FAILED, {
      reason: 'unexpected',
      file_size_bytes: file.size,
      context: analyticsContext,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro inesperado.',
    };
  }
};

// ---------------------------------------------------------------------------
// Annotations
// ---------------------------------------------------------------------------

export const createAnnotation = async (
  params: AnnotationCreateParams
): Promise<{ success: boolean; annotation?: VideoAnnotation; error?: string }> => {
  const { videoId, timestampSeconds, type, contentText, audioBlob, audioDurationSeconds } = params;

  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.user) {
      return { success: false, error: 'Usuário não autenticado.' };
    }

    const user = session.user;
    let audioUrl: string | undefined;

    if (type === 'voice_over' && audioBlob) {
      const validation = validateAudioBlob(audioBlob);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      const audioPath = `${videoId}/${user.id}/${Date.now()}.webm`;
      const { data: audioData, error: audioError } = await supabase.storage
        .from(VIDEO_AUDIO_BUCKET)
        .upload(audioPath, audioBlob, { contentType: audioBlob.type });

      if (audioError) {
        return { success: false, error: 'Falha ao fazer upload do áudio.' };
      }

      // Salva o path — signed URL gerada no momento da reprodução
      audioUrl = audioData.path;
    }

    const { data, error } = await supabase
      .from('video_annotations')
      .insert({
        video_id: videoId,
        author_id: user.id,
        timestamp_seconds: timestampSeconds,
        type,
        content_text: contentText ?? null,
        audio_url: audioUrl ?? null,
        audio_duration_seconds: audioDurationSeconds ?? null,
      })
      .select()
      .single();

    if (error) {
      return { success: false, error: 'Falha ao salvar anotação.' };
    }

    return { success: true, annotation: data as VideoAnnotation };
  } catch (error) {
    console.error('createAnnotation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro inesperado.',
    };
  }
};

export const deleteAnnotation = async (
  annotationId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('video_annotations')
      .delete()
      .eq('id', annotationId);

    if (error) return { success: false, error: 'Falha ao deletar anotação.' };
    return { success: true };
  } catch (error) {
    console.error('deleteAnnotation error:', error);
    return { success: false, error: 'Erro inesperado.' };
  }
};

// ---------------------------------------------------------------------------
// Video status
// ---------------------------------------------------------------------------

export const updateVideoStatus = async (
  videoId: string,
  status: TrainingVideo['status']
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('training_videos')
      .update({ status })
      .eq('id', videoId);

    if (error) return { success: false, error: 'Falha ao atualizar status.' };
    return { success: true };
  } catch (error) {
    console.error('updateVideoStatus error:', error);
    return { success: false, error: 'Erro inesperado.' };
  }
};

// ---------------------------------------------------------------------------
// Signed URLs
// ---------------------------------------------------------------------------

/**
 * Extrai o path relativo ao bucket a partir de uma URL completa do Supabase Storage.
 * Suporta URLs legadas no formato /object/public/{bucket}/{path}
 * e URLs assinadas no formato /object/sign/{bucket}/{path}.
 */
const extractStoragePath = (urlOrPath: string, bucket: string): string => {
  if (!urlOrPath.startsWith('http')) return urlOrPath;
  const markers = [`/object/public/${bucket}/`, `/object/sign/${bucket}/`];
  for (const marker of markers) {
    const idx = urlOrPath.indexOf(marker);
    if (idx !== -1) return urlOrPath.slice(idx + marker.length).split('?')[0];
  }
  return urlOrPath;
};

export const getSignedVideoUrl = async (
  path: string
): Promise<string | null> => {
  const storagePath = extractStoragePath(path, TRAINING_VIDEOS_BUCKET);

  const { data, error } = await supabase.storage
    .from(TRAINING_VIDEOS_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);

  if (error) {
    console.error('getSignedVideoUrl error:', error);
    return null;
  }
  return data.signedUrl;
};

export const getSignedAudioUrl = async (
  path: string
): Promise<string | null> => {
  const storagePath = extractStoragePath(path, VIDEO_AUDIO_BUCKET);

  const { data, error } = await supabase.storage
    .from(VIDEO_AUDIO_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);

  if (error) {
    console.error('getSignedAudioUrl error:', error);
    return null;
  }
  return data.signedUrl;
};
